import { useState } from 'react';
import { useBasket } from '../context/BasketContext.jsx';
import { useCurrency } from '../context/CurrencyContext.jsx';
import { useStoreUI } from '../context/StoreUIContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { avatarUrl } from '../lib/minecraft.js';
import { QuantityStepper } from './QuantityStepper.jsx';
import '../styles/components/BasketSidebarStyle.css';

export function BasketSidebar({ onCheckout, isCheckoutLaunching }) {
  const { isBasketOpen, closeBasket } = useStoreUI();
  const basket = useBasket();
  const { formatPrice } = useCurrency();
  const { showToast } = useToast();

  useEscapeKey(closeBasket, isBasketOpen);

  const {
    items,
    count,
    basePrice,
    salesTax,
    totalPrice,
    currency,
    coupon,
    loggedIn,
    username,
    isMutating,
  } = basket;
  const discountAmount = Math.max(0, basePrice + salesTax - totalPrice);
  const hasDiscount = Boolean(coupon) && discountAmount > 0;
  const isEmpty = items.length === 0;

  const handleRemove = async (packageId) => {
    const result = await basket.removeItem(packageId);

    if (result.success) showToast('Removed from basket.', 4000);
    else showToast(result.error || 'Could not remove this item.', 5000, 'error');
  };

  const handleQuantity = async (packageId, quantity) => {
    const result = await basket.setItemQuantity(packageId, quantity);

    if (result.success) showToast('Basket updated.', 3000);
    else showToast(result.error || 'Could not change quantity.', 5000, 'error');
    return result;
  };

  const handleLogout = async () => {
    await basket.logout();
    closeBasket();
  };

  return (
    <>
      <aside
        className={`basket-sidebar${isBasketOpen ? ' active' : ''}`}
        aria-label="Shopping basket"
        aria-hidden={!isBasketOpen}
      >
        <div className="basket-sidebar-header">
          <div className="basket-sidebar-user">
            <img
              src={avatarUrl(username, 88)}
              alt=""
              width="44"
              height="44"
              decoding="async"
            />
            <div className="basket-sidebar-user-meta">
              <h3>{loggedIn ? `${username}'s Basket` : 'Your Basket'}</h3>
              <span className="muted">{loggedIn ? 'Logged in' : 'Not logged in'}</span>
            </div>
          </div>
          <div className="basket-sidebar-actions">
            <button
              type="button"
              className="basket-icon-btn"
              aria-label="Close basket"
              title="Close"
              onClick={closeBasket}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div className="basket-sidebar-content">
          <ul className="cart-list" aria-live="polite" style={{ display: isEmpty ? 'none' : 'flex' }}>
            {items.map((item) => {
              const price = Number(item.price);
              const qty = Math.max(1, Number(item.qty) || 1);
              const maxQty = Math.max(1, Number(item.max_qty) || 1);

              const showStepper = !item.is_rank && maxQty > 1;

              return (
                <li key={item.id} className="cart-item">
                  <span className="cart-item-icon">
                    <i className="fa-solid fa-cube" />
                  </span>
                  <div className="cart-item-info">
                    <span className="cart-item-name">{item.name}</span>
                    <span className="cart-item-type">{item.type || 'package'}</span>
                    {item.gift_recipient && <span className="cart-item-recipient"><i className="fa-solid fa-gift" aria-hidden="true" /> Gift for {item.gift_recipient}</span>}
                    {showStepper && (
                      <QuantityStepper
                        value={qty}
                        max={maxQty}
                        label={`Quantity of ${item.name}`}
                        onChange={(next) => handleQuantity(item.id, next)}
                        disabled={isMutating}
                      />
                    )}
                  </div>

                  <span className="cart-item-price">
                    {formatPrice(price * qty, currency)}
                    {qty > 1 && (
                      <small className="cart-item-unit">{formatPrice(price, currency)} each</small>
                    )}
                  </span>

                  <button
                    type="button"
                    className="remove-item-btn"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => handleRemove(item.id)}
                    disabled={isMutating}
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                </li>
              );
            })}
          </ul>

          <div className={`basket-empty${isEmpty ? ' visible' : ''}`}>
            <i className="fa-solid fa-cart-shopping" />
            <p>Your basket is empty.</p>
            <span>Browse the categories to add packages.</span>
          </div>
        </div>

        <div className="basket-sidebar-footer">
          <CouponField hidden={isEmpty} />
          <dl className="cart-summary">
            {hasDiscount ? (
              <>
                <div className="cart-summary-row">
                  <dt>Subtotal</dt>
                  <dd>{formatPrice(basePrice, currency)}</dd>
                </div>

                <div className="cart-summary-row cart-summary-row--discount">
                  <dt>Discount</dt>
                  <dd>−{formatPrice(discountAmount, currency)}</dd>
                </div>
              </>
            ) : null}

            {salesTax > 0 && (
              <div className="cart-summary-row">
                <dt>Tax</dt>
                <dd>{formatPrice(salesTax, currency)}</dd>
              </div>
            )}

            <div className="cart-summary-row cart-summary-row--total">
              <dt>Total</dt>
              <dd aria-live="polite">{formatPrice(totalPrice, currency)}</dd>
            </div>
          </dl>

          <button
            type="button"
            className="checkout-btn"
            disabled={isCheckoutLaunching || isMutating}
            onClick={() => {
              if (count === 0) {
                showToast('Your basket is empty.', 4000, 'error');
                return;
              }
              onCheckout();
            }}
          >
            {isCheckoutLaunching ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                <span>Opening checkout…</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-credit-card" />
                <span>Secure Checkout</span>
              </>
            )}
          </button>

          <button type="button" className="logout-btn" onClick={handleLogout} disabled={isMutating}>
            <i className="fa-solid fa-arrow-right-from-bracket" />
            Logout
          </button>

          <p className="checkout-disclaimer">
            <i className="fa-solid fa-lock" />
            Powered by Tebex — secure checkout, your card details never touch our server.
          </p>
        </div>
      </aside>

      <div className={`basket-overlay${isBasketOpen ? ' active' : ''}`} onClick={closeBasket} />
    </>
  );
}


function CouponField({ hidden }) {
  const { coupon, applyCoupon, removeCoupon, isMutating } = useBasket();
  const { showToast } = useToast();
  const [code, setCode] = useState('');
  const [isApplying, setApplying] = useState(false);

  const handleApply = async (event) => {
    event.preventDefault();

    const trimmed = code.trim().toUpperCase();

    if (!trimmed) return;

    setApplying(true);
    const result = await applyCoupon(trimmed);
    setApplying(false);

    if (!result.success) {
      showToast(result.error || 'Invalid coupon.', 5000, 'error');
      return;
    }

    setCode('');
    showToast(`Coupon ${trimmed} applied.`, 4000);
  };

  const handleRemove = async () => {
    await removeCoupon(coupon.code);
    showToast('Coupon removed.', 3000);
  };

  if (coupon) {
    return (
      <div className="coupon-field" hidden={hidden}>
        <div className="coupon-box coupon-box--applied">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          <input
            type="text"
            className="coupon-applied-code"
            value={coupon.code}
            readOnly
            aria-label={`Coupon ${coupon.code} applied`}
          />
        </div>

        <button
          type="button"
          className="coupon-btn coupon-btn--remove"
          aria-label={`Remove coupon ${coupon.code}`}
          title="Remove coupon"
          onClick={handleRemove}
          disabled={isMutating}
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
    );
  }

  return (
    <form className="coupon-field" autoComplete="off" hidden={hidden} onSubmit={handleApply}>
      <div className="coupon-box">
        <i className="fa-solid fa-tag" aria-hidden="true" />
        <input
          type="text"
          className="coupon-input"
          placeholder="Discount code"
          aria-label="Discount code"
          maxLength={64}
          autoComplete="off"
          spellCheck="false"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </div>

      <button type="submit" className="coupon-btn" disabled={isApplying || isMutating || !code.trim()}>
        {isApplying ? <i className="fa-solid fa-spinner fa-spin" /> : 'Apply'}
      </button>
    </form>
  );
}
